import { useContext, useMemo, useState } from "react";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../../common/panels/TableView/TableView";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import { MoreDropdown } from "../../../common/buttons/MoreDropdown/MoreDropdown";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { NotSet } from "../../../common/misc/UIconstants";
import { LabNotebook, LabNotebookFilters } from "../../types/LabNotebook";
import { LabNotebookFilterBar, LabNotebookFilterForm } from "./LabNotebooksFilterBar";
import { DateTimeRenderer } from "../../../common/datetime/DateTimeFormatter";
import { Modal } from "../../common/ELNModal/ELNModal";
import { GetPersons, TableArrayRenderer } from "../../../common/misc/EntityRenders/EntityRenderer";
import { LinkButton } from "../../../common/buttons/LinkButton/LinkButton";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { LabNotebookAddForm } from "../LabNotebookViews";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import { EntityTableRestoreButton } from "../../../common/entity/entityComponents/EntityTableRestoreButton";
import { EntityTableSoftDeletableButton } from "../../../common/entity/entityComponents/EntityTableSoftDeletableButton";
import { EntityTableEditButton } from "../../../common/entity/entityComponents/EntityTableEditButton";
import { GenericEntityTableComplianceDropdown } from "../../../Entities/GenericComponents/common/GenericEntityTable";
import { NestedDropdown } from "../../../common/buttons/NestedDropdown/NestedDropdown";
import { ExportCsvButton } from "../../../common/tables/ExportCsvButton/ExportCsvButton";
import { ExportPdfButton } from "../../../common/tables/ExportPdfButton/ExportPdfButton";
import { labNotebookEntriesConstants, LabNotebookEntry, LabNotebookEntryFilters } from "../../types/LabNotebookEntry";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";

export const switchLabNotebooksDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<LabNotebookFilters>>[] | null | undefined
): SortState<InferSortByOptions<LabNotebookFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;
  return [
    {
      sortDirection: "DESC",
      headerId: "default-modifiedBy",
      property: "MODIFIED_ON",
    },
  ];
};

export const defaultLabNotebooksFilters: LabNotebookFilters = {
  sortBy: [
    {
      property: "MODIFIED_ON",
      sortDirection: "DESC",
    },
  ],
};

export const useLabNotebooksTableDefaults = ({ fieldLabels }: UseEntityTableDefaultProps<"notebooks">) => {
  const { session } = useContext(SessionContext);

  const defaults: ColumnsSettings<LabNotebook> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-description": { pos: 2, active: true, header: fieldLabels.description, property: "description" },
      "default-projects": { pos: 4, active: true, header: fieldLabels.projects, property: "projects" },
      "default-enteredOn": { pos: 5, active: true, header: fieldLabels.enteredOn, property: "enteredOn" },
      "default-enteredBy": { pos: 6, active: true, header: fieldLabels.enteredBy, property: "enteredBy" },
      "default-modifiedOn": { pos: 7, active: true, header: fieldLabels.modifiedOn, property: "modifiedOn" },
      "default-modifiedBy": { pos: 8, active: true, header: fieldLabels.modifiedBy, property: "modifiedBy" },
    }),
    [fieldLabels]
  );

  const tabStoreDefaults: TableTabsDict<LabNotebook, LabNotebookFilters, LabNotebookFilterForm> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultLabNotebooksFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
      mydata: {
        tabId: "mydata",
        type: "fixed",
        label: "My notebooks",
        title: "My notebooks",
        icon: "house",
        align: "left",
        xPos: 1,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { enteredByIds: session?.userId ? [session.userId] : null },
          sidebarFilters: {},
        },
      },
      trash: {
        tabId: "trash",
        type: "fixed",
        label: "Trash",
        title: "Trash",
        icon: "trash",
        align: "left",
        xPos: 3,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultLabNotebooksFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: { isSoftDeleted: true },
          sidebarFilters: {},
        },
      },
    }),
    [session?.userId]
  );

  return { defaults, tabStoreDefaults };
};

export const useLabNotebooksTableColumns: UseEntityTableColumns<"notebooks"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"notebooks">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<LabNotebook> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => (
          <span style={{ color: "var(--gray-400)" }}>
            <samp>{row.id}</samp>
          </span>
        ),
        width: 200,
        align: "right",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        id: "default-name",
        Header: fieldLabels.name,
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden", alignItems: "center" }}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                <LucideIcon name={`${!!row.isLocked ? "book" : "book-open"}`} color={"var(--primary)"} /> {row.name}
              </span>
            </div>
          </div>
        ),
        width: 300,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        id: "default-description",
        Header: fieldLabels.description,
        accessor: (row) => (
          <div
            style={{ display: "flex", flexFlow: "row nowrap", gap: "5px", overflow: "hidden" }}
            data-toggle="tooltip"
            title={row.description || "No description"}
          >
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ fontWeight: 500, whiteSpace: "nowrap", marginRight: "5px" }}>
                {row.description || NotSet}
              </span>
            </div>
          </div>
        ),
        width: 300,
        align: "left",
      },
      {
        id: "default-projects",
        Header: fieldLabels.projects,
        accessor: (row) => <TableArrayRenderer values={row.projects}>{(value) => value.name}</TableArrayRenderer>,
        width: 200,
        align: "left",
      },
      {
        id: "default-enteredOn",
        Header: fieldLabels.enteredOn,
        accessor: (row) => <DateTimeRenderer date={row.enteredOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_ON", sort, setSort),
      },
      {
        id: "default-enteredBy",
        Header: fieldLabels.enteredBy,
        accessor: (row) => <GetPersons persons={[row.enteredBy]} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("ENTERED_BY", sort, setSort),
      },
      {
        id: "default-modifiedOn",
        Header: fieldLabels.modifiedOn,
        accessor: (row) => <DateTimeRenderer date={row.modifiedOn} includeElapsed={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_ON", sort, setSort),
      },
      {
        id: "default-modifiedBy",
        Header: fieldLabels.modifiedBy,
        accessor: (row) => <GetPersons persons={[row.modifiedBy]} createLinks={false} />,
        width: 200,
        align: "left",
        ...getAsyncSortingFn("MODIFIED_BY", sort, setSort),
      },
    ];

    return headers;
  }, [
    fieldLabels.id,
    fieldLabels.name,
    fieldLabels.description,
    fieldLabels.projects,
    fieldLabels.enteredOn,
    fieldLabels.enteredBy,
    fieldLabels.modifiedOn,
    fieldLabels.modifiedBy,
    sort,
    setSort,
  ]);
  return { columns };
};

export const LabNotebooksTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityTableProps<"notebooks">) => {
  const [showModal, setShowModal] = useState(false);
  const [showCreateLabNotebookModal, setShowCreateLabNotebookModal] = useState<boolean>(false);

  const {
    bulkRestoreMutationAsync: bulkRestoreLabNotebooks,
    bulkDeleteMutationAsync: deleteLabNotebooks,
    isLoadingBulkRestoreMutation,
    isLoadingBulkDeleteMutation,
  } = entityApi;

  const { defaults, tabStoreDefaults } = useLabNotebooksTableDefaults({ fieldLabels });

  const { selection, resultsCount, selectionPermissions, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<LabNotebook>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    temporaryTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<LabNotebook, LabNotebookFilters, LabNotebookFilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue, onTabChange, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      currentTab,
      tabsLoading,
      filters,
      switchSortState: switchLabNotebooksDefaultSortState,
      dispatchTabStore,
    });

  const { columns } = useLabNotebooksTableColumns({ fieldLabels, entityConstants, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <>
      <TableView>
        <TableView.Head>
          <TableView.Head.Label>
            {/* <div>Lab notebooks {typeof resultsCount === "number" && <span className="badge">{resultsCount}</span>}</div> */}
            <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
          </TableView.Head.Label>
          <TableView.Head.Controls>
            <LinkButton
              linkTo={routes.getAddRoute}
              className="btn btn-primary"
              title={`Add ${entityConstants.entitySingular}`}
              disabled={!permissions.canCreate}
            >
              <LucideIcon name="plus" /> Add {entityConstants.entitySingular}
            </LinkButton>
          </TableView.Head.Controls>
        </TableView.Head>
        <TableView.Body>
          <TableView.Body.Sidebar>
            <LabNotebookFilterBar
              initialValues={sidebarFilters}
              dispatchTabStore={dispatchTabStore}
              tabsLoading={tabsLoading}
              currentTab={currentTab}
            />
          </TableView.Body.Sidebar>
          <TableView.Body.Content>
            <EntityTableTabs
              currentTab={currentTab}
              onTabChange={onTabChange}
              temporaryTabs={temporaryTabs}
              fixedTabs={fixedTabs}
              filters={{
                ...filters,
                isSoftDeleted: filters.isSoftDeleted || false,
                includeSoftDeleted: filters.includeSoftDeleted || false,
              }}
              sidebarFilters={sidebarFilters}
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              customTabs={customTabs}
              dispatchTabStore={dispatchTabStore}
              tabsLoading={tabsLoading}
              tabsModified={tabsModified}
            />
            <EntityTable>
              <EntityTable.Controls
                style={{
                  borderTop: "0px",
                  borderRadius: "0px",
                }}
              >
                <EntityFilterIndicator<LabNotebook, LabNotebookFilters>
                  filters={filters}
                  excludeFilters={{ includeSoftDeleted: (value) => !!value, isSoftDeleted: (value) => !!value }}
                />
                <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
                <EntityTableEditButton
                  entityConstants={entityConstants}
                  routes={routes}
                  permissions={permissions}
                  selection={selection}
                  selectionPermissions={selectionPermissions}
                />
                <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                  <NestedDropdown label="Share / Export" icon="share-2" disabled={selection.size === 0}>
                    <li>
                      <ExportCsvButton
                        entityConstants={entityConstants}
                        columnSettings={columnSetting}
                        filters={{
                          ids: selection.size > 0 ? Array.from(selection) : undefined,
                          ...memoizedFilters,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                    <li>
                      <ExportPdfButton<LabNotebookEntry, LabNotebookEntryFilters>
                        entityConstants={labNotebookEntriesConstants}
                        filters={{
                          labNotebookReferenceIds: selection.size > 0 ? Array.from(selection) : undefined,
                          searchTerm: debouncedSearchValue,
                        }}
                        disabled={selection.size === 0}
                      />
                    </li>
                  </NestedDropdown>

                  {/* Locking & Signing */}
                  <GenericEntityTableComplianceDropdown
                    entityTypeId={entityTypeId}
                    selection={selection}
                    selectionPermissions={selectionPermissions}
                    tableFunctionRef={functionRef}
                    currentTab={currentTab}
                    showSignButton
                  />
                  <li className="divider" />
                  {/* Trash */}
                  {currentTab === "trash" && (
                    <li>
                      <EntityTableRestoreButton
                        entityConstants={entityConstants}
                        selection={selection}
                        permissions={permissions}
                        selectionPermissions={selectionPermissions}
                        onClick={async () =>
                          await bulkRestoreLabNotebooks({ ids: Array.from(selection) }).catch(() => {})
                        }
                        loading={isLoadingBulkRestoreMutation}
                      />
                    </li>
                  )}
                  <li>
                    <EntityTableSoftDeletableButton
                      currentTab={currentTab}
                      entityConstants={entityConstants}
                      selection={selection}
                      permissions={permissions}
                      selectionPermissions={selectionPermissions}
                      onClick={() => setShowModal(true)}
                    />
                  </li>
                </MoreDropdown>
                <AlertModal
                  type={`${currentTab === "trash" ? "danger" : "warning"}`}
                  showModal={showModal}
                  setShowModal={setShowModal}
                  forceUserInput={currentTab === "trash"}
                  title={`${
                    selection.size === 0
                      ? `Select dataset to ${currentTab === "trash" ? "delete" : "trash"}`
                      : selection.size === 1
                      ? `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entitySingular}?`
                      : `${currentTab === "trash" ? "Delete" : "Trash"} the selected ${entityConstants.entityPlural}?`
                  }`}
                  description={`${
                    currentTab === "trash"
                      ? `Proceeding will permanently delete the selected ${entityConstants.entityPlural}.`
                      : `Proceeding will move the selected ${entityConstants.entityPlural} into trash.`
                  }`}
                  proceedLabel={`${currentTab === "trash" ? "Delete" : "Trash"}`}
                  onProceed={async () => {
                    await deleteLabNotebooks({
                      ids: Array.from(selection),
                      goBackOnSuccess: false,
                      showToast: false,
                      entityName: entityConstants.resource,
                      params: { deletePermanently: currentTab === "trash" },
                    }).catch((e) => {});
                    functionRef.current?.resetSelection();
                    setShowModal(false);
                  }}
                  loading={isLoadingBulkDeleteMutation}
                />
              </EntityTable.Controls>
              <EntityTable.Body<LabNotebook, LabNotebookFilters>
                functionRef={functionRef}
                entityConstants={entityConstants}
                filters={memoizedFilters}
                columns={columns}
                columnSelect
                columnSetting={columnSetting}
                columnWidths={columnWidths}
                defaultColumnSettings={defaults}
                dispatchTabStore={dispatchTabStore}
                setResultsCount={onCountChange}
                onSelectionChange={onSelectionChange}
                onSelectionPermissions={onSelectionPermissions}
                showPermissionColumn
                loading={tabsLoading}
              />
            </EntityTable>
          </TableView.Body.Content>
        </TableView.Body>
      </TableView>
      <Modal isOpen={showCreateLabNotebookModal} onClose={() => setShowCreateLabNotebookModal(false)}>
        <div style={{ overflow: "auto" }}>
          <div style={{ width: "50vw", minWidth: "max-content" }}>
            <LabNotebookAddForm
              onSuccess={() => setShowCreateLabNotebookModal(false)}
              onCancel={() => setShowCreateLabNotebookModal(false)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};
